
import { cn } from "@/lib/utils";
import { Tool } from "@/hooks/use-canvas-setup";
import { ReactNode, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Ruler } from "lucide-react";
import { IncisionTemplateItem } from "../components/IncisionTemplateItem";

interface MeasureToolsProps {
  activeTool: "ruler" | "angle" | null;
  onToolToggle: (tool: Tool) => void;
  icon: ReactNode;
  label: string;
}

export const MeasureTools = ({ 
  activeTool, 
  onToolToggle,
  icon,
  label
}: MeasureToolsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToolClick = (tool: "ruler" | "angle") => {
    if (activeTool === tool) {
      onToolToggle(null);
    } else {
      onToolToggle(tool);
    }
    setIsOpen(false); // Close the dropdown after selection
  };

  const isActive = activeTool === "ruler" || activeTool === "angle";

  return (
    <div className="toolbar-item">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "modern-toolbar-button",
              isActive && "active"
            )}
            aria-label={label}
            title={`Measurement Tools: ${activeTool || 'None'}`}
          >
            {icon}
            {isActive && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="modern-toolbar-dropdown p-1 rounded-xl"
        >
          <DropdownMenuLabel className="text-xs font-medium text-gray-500 px-2 py-1">
            Measurement Tools
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="mb-1" />
          <div className="p-1 space-y-0.5">
            <IncisionTemplateItem 
              icon={Ruler}
              label="Ruler"
              onClick={() => handleToolClick('ruler')}
              active={activeTool === "ruler"}
            />
            <div 
              onClick={() => handleToolClick('angle')}
              className={cn(
                "flex items-center gap-2 cursor-pointer py-2 px-2 rounded transition-all duration-200",
                activeTool === "angle" ? "bg-purple-50 text-purple-600" : "hover:bg-purple-50/50 hover:text-purple-600"
              )}
            >
              <span className="text-sm font-semibold h-5 w-5 flex items-center justify-center">A°</span>
              <span className="text-sm">Angle</span>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
